import { LoadType } from '@dicetechnology/vesper-sdk-web';
import { PkDialog, PkDivider, PkIcon } from '@uefadigital/panenka-react';
import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import clsx from 'clsx';
import { memo, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import { PlayerWithLock } from '../VesperPlayer/PlayerWithLock';
import VideoListItem from '../VideoListItem';

import type { BucketContainer, PlayListContainer } from '@apps/uefa-tv/types';
import Bounce from '@shared/Bouncer';
import { useWindowSize } from '@shared/hooks/useWindowSize';
import Image from '@shared/Image';
import { useTranslateFn } from '@shared/Translate';
import type { UefatvEditorialContent } from '@shared-types/uefatv.types';

import * as styles from './styles.module.scss';

const VideoList = <T extends BucketContainer | PlayListContainer>({
  video,
  onScrolled,
  setCurrentVideo,
  container,
  hasNextPage,
}: {
  video: UefatvEditorialContent;
  onScrolled?: () => void;
  setCurrentVideo: (video: UefatvEditorialContent) => void;
  container?: T;
  hasNextPage?: boolean;
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { items } = container ?? {};

  const topTitle = (container as PlayListContainer)?.title ?? (container as BucketContainer)?.name ?? '';

  const isPlaylist = !!container && 'title' in container;
  const topDescription = isPlaylist && container.description;
  const vodCount = isPlaylist ? container.vods?.length : 0;
  const picture = isPlaylist && container.coverUrl;
  const hideMetadata = container && 'hideMetadata' in container ? container.hideMetadata : false;

  const spyRef = useRef(undefined);
  const scrolled = useViewportSpy(spyRef);

  const [open, setOpen] = useState(false);
  const handleClick = (video: UefatvEditorialContent) => () => {
    setCurrentVideo(video);
  };

  useEffect(() => {
    scrolled && onScrolled && onScrolled();
  }, [scrolled, onScrolled]);

  const t = useTranslateFn();
  const { isMobile } = useWindowSize();

  if (!video) return null;

  const openDialog = () => {
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>{document.title.replace(/^(UEFA\.tv.+)/g, `${video.title} | $1`)}</title>
        <meta name='description' content={video.description} />
      </Helmet>
      {isMobile && !!picture && (
        <PkDialog onPkClose={closeDialog} isOpen={open} className={styles.dialog}>
          <div slot='header'>
            <Image src={picture} className={styles.img} alt={topTitle} title={topTitle} />
          </div>
          <div slot='body' className='pk-text--text-01 pk-m--l'>
            <h2 className='pk-font--base-bold pk-font-size-sm--xl2 pk-font-size--l pk-mb--xs'>{topTitle}</h2>
            <div className='pk-font-size--s pk-line-height--md'>{topDescription}</div>
            {!!vodCount && (
              <div>
                <PkDivider className={styles.divider} />
                <div className='pk-font--base-semibold pk-font-size--s'>{`${vodCount} ${vodCount === 1 ? t('video') : t('videos')}`}</div>
              </div>
            )}
          </div>
        </PkDialog>
      )}
      <div className='pk-container'>
        <div className='pk-col pk-col--span-00-4 pk-col--span-ss-4 pk-col--span-xs-4 pk-col--span-sm-8 pk-col--span-md-12 pk-col--span-lg-12 pk-mt-sm--xl2'>
          {!!topTitle && (
            <header className='pk-d--flex pk-flex-md--column pk-justify-content--between pk-col--header pk-mb-sm--xl2 pk-mb--s'>
              <h2 className='pk-font--base-bold pk-font-size-sm--xl2 pk-font-size--l pk-line-height--md'>{topTitle}</h2>
              {isMobile && !!picture && (
                <div className='pk-font--base-regular'>
                  <PkIcon name='spectator-basic-info' onClick={openDialog} />
                </div>
              )}
              {!isMobile && !!topDescription && (
                <div className='pk-line-height--md pk-font-size--s pk-text--text-01'>{topDescription}</div>
              )}
            </header>
          )}
          <div className='pk-col--content'>
            <div className={clsx({ [styles.container]: items?.length > 1 })}>
              <PlayerWithLock video={video} type={LoadType.VOD} />
              <div className={styles.details}>
                <p className={styles.title}>{video.title}</p>
                <p className={styles.description}>{video.description}</p>
              </div>

              {items?.length > 1 && (
                <div className={styles.listContainer}>
                  <div className={clsx('pk-d--flex pk-flex--column', styles.list)} role='list'>
                    {items?.map((item) => (
                      <VideoListItem
                        key={item.id}
                        item={item}
                        videoId={video.id}
                        handleClick={handleClick}
                        hideMetadata={!!hideMetadata}
                      />
                    ))}
                    <div ref={spyRef} className={styles.spyRef} />
                    {hasNextPage && <Bounce />}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(VideoList);
